/**
 * ClientInstallCard — One-click install card for an MCP client.
 * Shows connection status, auto-creates tokens, displays setup instructions.
 */

import React, { useState, useCallback } from 'react';
import {
  Check,
  Copy,
  ExternalLink,
  Loader2,
} from 'lucide-react';
import type { ApiKey } from '../../../api/auth';
import { useClientToken } from './useClientToken';

// ─── Config Builders ────────────────────────────────────────────
// Derive the endpoint from the host the dashboard is served on, so the prod
// tokens page (martha.arch.ie) shows martha.arch.ie/mcp, dev shows dev, etc.
const MCP_ENDPOINT =
  typeof window !== 'undefined' && window.location?.origin
    ? `${window.location.origin}/mcp`
    : 'https://martha.arch.ie/mcp';

/** The bare connector URL — for clients (Cowork, Claude Desktop) that add a
 * remote MCP server by URL and authenticate via OAuth (no token to paste). */
export function buildConnectorUrl(): string {
  return MCP_ENDPOINT;
}

/** Full Martha plugin (skills + commands + agents + bundled MCP) via the marketplace. */
export function buildClaudeCodePluginCommand(): string {
  return [
    'claude plugin marketplace add heyarchie-ai/martha',
    'claude plugin install martha@martha',
    '# Turn on automatic updates for the martha marketplace in Claude Code: run /plugin, open Marketplaces, choose martha and enable auto-update, or set autoUpdate to true on martha in ~/.claude/plugins/known_marketplaces.json',
  ].join('\n');
}

function buildMcpServerConfig(token: string) {
  return {
    type: 'http',
    url: MCP_ENDPOINT,
    headers: { Authorization: `Bearer ${token}` },
  };
}

/** HTTP config for clients that support remote MCP natively (Cursor, Zed, Claude Code) */
export function buildFullConfig(token: string): string {
  return JSON.stringify({ mcpServers: { martha: buildMcpServerConfig(token) } }, null, 2);
}

export function buildClaudeCodeCommand(token: string): string {
  const config = JSON.stringify(buildMcpServerConfig(token));
  return `claude mcp add-json martha '${config}'`;
}

/**
 * Claude Desktop uses stdio transport only — bridge via mcp-remote.
 * mcp-remote proxies the HTTP endpoint to a local stdio connection.
 */
export function buildDesktopConfig(token: string): string {
  return JSON.stringify({
    mcpServers: {
      martha: {
        command: 'npx',
        args: ['-y', 'mcp-remote', MCP_ENDPOINT, '--header', `Authorization: Bearer ${token}`],
      },
    },
  }, null, 2);
}

export function buildDesktopDeepLink(token: string): string {
  const config = JSON.stringify({ martha: { command: 'npx', args: ['-y', 'mcp-remote', MCP_ENDPOINT, '--header', `Authorization: Bearer ${token}`] } });
  return `claude://settings/mcp/install?config=${encodeURIComponent(btoa(config))}`;
}

// ─── Types ──────────────────────────────────────────────────────

export interface ClientDef {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Build the primary copyable text (command or config) */
  buildInstruction: (token: string) => string;
  /** Optional hint below the code block */
  hint?: string;
  /** If set, show a deep-link button instead of / in addition to copy */
  buildDeepLink?: (token: string) => string;
  /** File path hint for manual config */
  configPaths?: { label: string; path: string }[];
  /** Auth is OAuth/URL-based — the instruction never embeds a key, so hide "Use existing key" */
  tokenless?: boolean;
}

interface ClientInstallCardProps {
  client: ClientDef;
  keys: ApiKey[];
  activeKeyCount: number;
  onKeysChanged: () => Promise<void>;
}

// ─── CopyButton ─────────────────────────────────────────────────
function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [text]);

  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all
        text-muted hover:text-text hover:bg-surface-subtle active:scale-95"
    >
      {copied ? (
        <>
          <Check className="w-3.5 h-3.5 text-green-500" />
          <span className="text-green-600">Copied</span>
        </>
      ) : (
        <>
          <Copy className="w-3.5 h-3.5" />
          <span>{label}</span>
        </>
      )}
    </button>
  );
}

// ─── Status Badge ───────────────────────────────────────────────
function StatusBadge({ status }: { status: 'connected' | 'pending' | 'not-connected' }) {
  const config = {
    connected: { label: 'Connected', bg: 'bg-green-50', text: 'text-green-700', dot: 'bg-green-500' },
    pending: { label: 'Pending', bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
    'not-connected': { label: 'Not connected', bg: 'bg-surface-muted', text: 'text-muted', dot: 'bg-neutral-400' },
  }[status];

  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium ${config.bg} ${config.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
      {config.label}
    </span>
  );
}

// ─── Main Card ──────────────────────────────────────────────────
export const ClientInstallCard: React.FC<ClientInstallCardProps> = ({
  client,
  keys,
  activeKeyCount,
  onKeysChanged,
}) => {
  const { existingToken, status, createToken, revealToken, freshKey, creating, error } =
    useClientToken(client.name, keys, onKeysChanged);

  const atLimit = activeKeyCount >= 10;
  const Icon = client.icon;

  // "Use existing key" — keys are hashed server-side (revealed once at
  // creation), so reuse means the user pastes theirs. Client-side only: the
  // pasted key is templated into the instruction, never sent anywhere.
  const [showPaste, setShowPaste] = useState(false);
  const [pasteDraft, setPasteDraft] = useState('');
  const [pastedKey, setPastedKey] = useState<string | null>(null);

  // After install (or key paste): show instruction with the real token
  const showInstruction = freshKey != null || pastedKey != null;
  const tokenForDisplay = freshKey ?? pastedKey ?? '';

  return (
    <div className="border border-line rounded-xl p-5 bg-white hover:border-line-strong transition-colors">
      {/* Header row */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-surface-subtle flex items-center justify-center">
            <Icon className="w-5 h-5 text-text" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-text">{client.name}</h3>
            <p className="text-xs text-muted">{client.description}</p>
          </div>
        </div>
        <StatusBadge status={status} />
      </div>

      {/* Error */}
      {error && (
        <div className="mt-3 p-2 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700">
          {error}
        </div>
      )}

      {/* Not yet installed — show Install button */}
      {!showInstruction && (
        <div className="mt-4 space-y-2">
          <div className="flex items-center justify-end gap-2">
            {existingToken && !freshKey && (
              <span className="text-[11px] text-subtle mr-auto">
                Token exists — click to reinstall with a new key
              </span>
            )}
            {!client.tokenless && (
              <button
                onClick={async () => {
                  // Matching key stored encrypted? Reveal it server-side — no paste.
                  const k = await revealToken();
                  if (k) setPastedKey(k);
                  else setShowPaste((v) => !v);
                }}
                className="inline-flex items-center px-3 py-2 rounded-lg text-xs font-medium transition-colors
                  text-muted hover:text-text hover:bg-surface-subtle"
              >
                Use existing key
              </button>
            )}
            <button
              onClick={createToken}
              disabled={creating || atLimit}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-colors
                bg-neutral-900 text-white hover:bg-neutral-800 active:scale-[0.97]
                disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {creating ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Creating...
                </>
              ) : existingToken ? (
                'Reinstall'
              ) : (
                'Install Martha'
              )}
            </button>
          </div>
          {showPaste && (
            <form
              className="flex items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                const k = pasteDraft.trim();
                if (k) setPastedKey(k);
              }}
            >
              <input
                type="password"
                value={pasteDraft}
                onChange={(e) => setPasteDraft(e.target.value)}
                placeholder="mk_..."
                autoComplete="off"
                spellCheck={false}
                className="flex-1 px-3 py-2 rounded-lg border border-line bg-white text-xs font-mono text-text
                  placeholder:text-subtle focus:outline-none focus:border-line-strong"
              />
              <button
                type="submit"
                disabled={!pasteDraft.trim()}
                className="inline-flex items-center px-3 py-2 rounded-lg text-xs font-medium transition-colors
                  bg-neutral-900 text-white hover:bg-neutral-800 active:scale-[0.97]
                  disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Continue
              </button>
            </form>
          )}
        </div>
      )}

      {/* Post-install: show instruction */}
      {showInstruction && (
        <div className="mt-4 space-y-3">
          <p className="text-xs text-muted">
            {freshKey ? 'Token created.' : 'Using your existing key.'}{' '}
            {client.buildDeepLink ? 'Try one-click install, or copy the config below:' : 'Run this in your terminal:'}
          </p>

          {/* Deep link button (Claude Desktop) */}
          {client.buildDeepLink && (
            <div className="flex items-center gap-2 flex-wrap">
              <a
                href={client.buildDeepLink(tokenForDisplay)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium
                  bg-neutral-900 text-white hover:bg-neutral-800 transition-colors active:scale-[0.98]"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Open in {client.name}
              </a>
              <span className="text-[11px] text-subtle">— or copy config below</span>
            </div>
          )}

          {/* Config paths (manual fallback) */}
          {client.configPaths && (
            <div className="text-xs text-muted space-y-0.5">
              <p className="text-subtle font-medium">
                {client.buildDeepLink ? 'Manual setup — paste config into:' : 'Config file:'}
              </p>
              {client.configPaths.map((cp) => (
                <p key={cp.label}>
                  <strong>{cp.label}:</strong>{' '}
                  <code className="font-mono bg-surface-subtle px-1 rounded text-muted">{cp.path}</code>
                </p>
              ))}
            </div>
          )}

          {/* Code block */}
          <div className="relative group">
            <pre className="bg-neutral-900 text-neutral-100 rounded-lg p-4 pr-20 text-sm font-mono overflow-x-auto leading-relaxed whitespace-pre-wrap break-all">
              {client.buildInstruction(tokenForDisplay)}
            </pre>
            <div className="absolute top-2 right-2">
              <CopyButton text={client.buildInstruction(tokenForDisplay)} />
            </div>
          </div>

          {/* Hint */}
          {client.hint && (
            <p className="text-xs text-subtle">{client.hint}</p>
          )}
        </div>
      )}
    </div>
  );
};
